import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Checkbox, InputPrefix, InputSuffix } from '@progress/kendo-react-inputs';
import CustomerContactInput from "components/CustomerContactInput";
import CustomerInput from "components/CustomerInput";
import SimpleDatePicker from 'components/SimpleDatePicker';
import SimpleTimePicker from 'components/SimpleTimePicker';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchApi } from 'services/api';
import { IDNameViewModel } from 'TypeGen/id-name-view-model';
import { StopEditViewModel } from "TypeGen/Order/stop-edit-view-model";
import { StopType } from 'TypeGen/stop-type';
import { StopVerb } from "TypeGen/stop-verb";
import { IDName } from 'types/idname';
import SingleLocationAutoComplete from "views/PCMiler/SingleLocationAutoComplete";

type Props = {
  locationLabel?: string;
  orderStop: StopEditViewModel;
  setOrderStop: React.Dispatch<React.SetStateAction<StopEditViewModel>>;
  reasonsLate?: IDNameViewModel[];
  requireReasonLate?: boolean;
  resolveStopNote?: string;
};

const StopEditUI: React.FC<Props> = ({ orderStop, setOrderStop, reasonsLate, requireReasonLate, ...props }) => {
  const [instructions, setInstructions] = useState<Array<{ Display: number, Instruction: string }>>([]);
  const [originalScheduledDateTime,] = React.useState(orderStop.ScheduledDateTime);

  const getCustomerInstructions = useCallback(() => {
    if (orderStop?.CustomerID > 0) {
      fetchApi(`/api/Customer/SpecialInstructions/${orderStop.CustomerID}`)
        .then((response: Array<{ Type: number, Display: number, Instruction: string }>) => {
          const instructions = response
            .filter(x => (orderStop.StopType == StopType.Pickup && x.Type === 83) || (orderStop.StopType == StopType.Delivery && x.Type === 67))
            .map(function (x) { return { Display: x.Display, Instruction: x.Instruction } });

          const uniqueInstructions = Array.from(new Set(instructions.map(x => x.Instruction)));

          const distinctInstructionRecords = Array.from(uniqueInstructions, Instruction => {
            return instructions.find(x => x.Instruction === Instruction);
          });

          setInstructions(distinctInstructionRecords);

        });
    }
  }, [orderStop?.CustomerID, orderStop?.StopType]);

  useEffect(() => {
    getCustomerInstructions();
  }, [getCustomerInstructions, orderStop?.CustomerID]);

  const setStopVerb = (stopVerb: StopVerb) => {
    setOrderStop({
      ...orderStop,
      StopVerb: stopVerb,
      EarliestStopDateTime: stopVerb === StopVerb.WINDOW ? orderStop.ScheduledDateTime : undefined,
      LatestStopDateTime: stopVerb === StopVerb.WINDOW ? orderStop.ScheduledDateTime : undefined
    });
  }

  const fetchAdjustedScheduledDateTimeDiff = () => {
    const _originalScheduledDateTime = dayjs(originalScheduledDateTime);
    //orderStop.ScheduledDateTime
    const scheduledDateTime = dayjs(dateTime);

    const duration = dayjs.duration(scheduledDateTime.diff(_originalScheduledDateTime));

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    return `${days} day(s) ${hours} hour(s) ${minutes} minute(s)`;
  }

  const updateDate = (date: Date | null) => {
    if (date == null) return;
    updateDateTime(dayjs(date).hour(new Date(orderStop.ScheduledDateTime).getHours()).minute(new Date(orderStop.ScheduledDateTime).getMinutes()).startOf('minute'));
  }

  const updateTime = (time: Date | null) => {
    if (time == null) return;
    updateDateTime(dayjs(new Date(orderStop.ScheduledDateTime)).hour(time.getHours()).minute(time.getMinutes()).startOf('minute'));
  }

  const updateDateTime = (value: dayjs.Dayjs) => {
    const newDateTime = value.format("MM/DD/YYYY HH:mm");
    if (orderStop.ScheduledDateTime !== newDateTime) {
      setOrderStop({
        ...orderStop,
        ScheduledDateTime: newDateTime,
        EarliestStopDateTime: (orderStop.StopVerb !== StopVerb.WINDOW || dayjs(newDateTime).isSameOrBefore(orderStop.LatestStopDateTime)) ? orderStop.LatestStopDateTime : newDateTime,
        LatestStopDateTime: (orderStop.StopVerb !== StopVerb.WINDOW || dayjs(newDateTime).isSameOrAfter(orderStop.EarliestStopDateTime)) ? orderStop.EarliestStopDateTime : newDateTime,
        AdjustSubsequentStops: orderStop.ShowAdjustSubsequentStopsOption === false ? null : orderStop.AdjustSubsequentStops == null ? false : orderStop.AdjustSubsequentStops
      });
    }
  }

  const updateEarliestDate = (date: Date | null) => {
    if (date == null) return;
    updateEarliestDateTime(dayjs(date).hour(new Date(orderStop.EarliestStopDateTime).getHours()).minute(new Date(orderStop.EarliestStopDateTime).getMinutes()).startOf('minute'));
  }

  const updateEarliestTime = (time: Date | null) => {
    if (time == null) return;
    updateEarliestDateTime(dayjs(new Date(orderStop.EarliestStopDateTime)).hour(time.getHours()).minute(time.getMinutes()).startOf('minute'));
  }

  const updateEarliestDateTime = (value: dayjs.Dayjs) => {
    const newDateTime = value.format("MM/DD/YYYY HH:mm");
    if (orderStop.EarliestStopDateTime !== newDateTime) {
      setOrderStop({
        ...orderStop,
        EarliestStopDateTime: newDateTime
      });
    }
  }

  const updateSubsequentStops = (value: boolean) => {
    setOrderStop({
      ...orderStop,
      AdjustSubsequentStops: value
    });
  }

  const updateLatestDate = (date: Date | null) => {
    if (date == null) return;
    updateLatestDateTime(dayjs(date).hour(new Date(orderStop.LatestStopDateTime).getHours()).minute(new Date(orderStop.LatestStopDateTime).getMinutes()).startOf('minute'));
  }

  const updateLatestTime = (time: Date | null) => {
    if (time == null) return;
    updateLatestDateTime(dayjs(new Date(orderStop.LatestStopDateTime)).hour(time.getHours()).minute(time.getMinutes()).startOf('minute'));
  }

  const updateLatestDateTime = (value: dayjs.Dayjs) => {
    const newDateTime = value.format("MM/DD/YYYY HH:mm");
    if (orderStop.LatestStopDateTime !== newDateTime) {
      setOrderStop({
        ...orderStop,
        LatestStopDateTime: newDateTime
      });
    }
  }

  const renderTimeInputGroup = () => {
    return <InputSuffix>
      <DropDownList
        style={{ width: 128 }}
        data={orderStop.StopVerbs}
        value={orderStop.StopVerbs.find(x => x.ID === orderStop.StopVerb)}
        textField='Name'
        dataItemKey='ID'
        fillMode="flat"
        onChange={(e) => setStopVerb(e.target.value.ID)}
      />
    </InputSuffix>;
  }

  const populateCustomerContacts = useCallback((customerId: number) => {
    if (!customerId) return;
    fetchApi('/api/Quote/CustomerContacts', { CustomerID: customerId }, 'POST')
      .then((response: { PerferredContactID: number, Contacts: IDName[] }) => {
        setOrderStop(prev => ({
          ...prev,
          CustomerContacts: response.Contacts,
          CustomerContactID: prev.CustomerContactID || response.PerferredContactID || null
        }));
      });
  }, [setOrderStop]);

  const instructionColor = (display: number) => {
    switch (display) {
      case 82:
        return { className: "text-danger" };
      case 71:
        return { className: "text-success" };
      case 89:
        return { className: "text-warning" };
      case 66:
        return { className: "text-primary" };
    }
    return null;
  }

  const renderInstructions = () => {
    if (instructions.length === 0 || !orderStop.CustomerID) return null;
    return <p>
      {instructions.map((instruction, index) => {
        return <React.Fragment key={index}>
          <span {...instructionColor(instruction.Display)}>{orderStop.CustomerNumber}: {instruction.Instruction}</span>
          <br />
        </React.Fragment>;
      })}
    </p>
  }

  const dateTime = dayjs(orderStop.ScheduledDateTime);
  const latestDateTime = orderStop.StopVerb === StopVerb.WINDOW ? dayjs(orderStop.LatestStopDateTime) : undefined;
  const earliestDateTime = orderStop.StopVerb === StopVerb.WINDOW ? dayjs(orderStop.EarliestStopDateTime) : undefined;

  return <>
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">{props.locationLabel ?? "Location"}</label>
      </div>
      <div className="form-group col-md-10">
        <div className="input-group">
          <CustomerInput
            placeholder='Company'
            CustomerID={orderStop.CustomerID}
            CustomerNumber={orderStop.CustomerNumber}
            StopNote={props.resolveStopNote}
            City={orderStop.City}
            State={orderStop.State}
            ZipCode={orderStop.ZipCode}
            onChange={(e) => {
              setOrderStop({
                ...orderStop,
                City: e.City,
                State: e.State,
                ZipCode: e.ZipCode,
                LocalIanaTimeZoneID: e.LocalIanaTimeZoneID,
                CustomerID: e.CustomerID,
                CustomerNumber: e.CustomerNumber,
                CustomerContactID: null,
                CustomerContacts: [],
              });
              populateCustomerContacts(e.CustomerID);
            }}
          />
        </div>
      </div>
    </div>
    <div className="form-row">
      <div className="col-md-10 offset-md-2">
        <div className="form-group">
          <SingleLocationAutoComplete
            value={(orderStop.ZipCode || orderStop.City) && `${orderStop.ZipCode} ${orderStop.City}, ${orderStop.State}`}
            autoCompleteProps={{ clearButton: true, placeholder: 'Enter City or Zip' }}
            onSelected={(e) => setOrderStop({
              ...orderStop,
              City: e?.Address.City,
              State: e?.Address.State,
              ZipCode: e?.Address.Zip,
              LocalIanaTimeZoneID: null,
              CustomerID: null,
              CustomerNumber: '',
              CustomerContactID: null,
            })}
          />
        </div>
        {renderInstructions()}
      </div>
    </div>
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">Scheduled</label>
      </div>
      <div className="form-group col-md-5">
        <SimpleDatePicker
          inputProps={{
            prefix: () => <InputPrefix>
              <Button disabled>EST</Button>
            </InputPrefix>
          }}
          value={dateTime.toDate()}
          onChange={updateDate}
        />
      </div>
      <div className="form-group col-md-5">
        <SimpleTimePicker
          value={dateTime.toDate()}
          onChange={updateTime}
          inputProps={{
            suffix: renderTimeInputGroup
          }}
        />
      </div>
    </div>
    {orderStop.LocalIanaTimeZoneID != null && <div className="form-row">
      <div className="form-group col-md-10 offset-md-2" title={orderStop.LocalIanaTimeZoneID}>
        Local Time: {dateTime.tz(orderStop.LocalIanaTimeZoneID).format("MM/DD/YYYY HH:mm")}
      </div>
    </div>}

    {orderStop.AdjustSubsequentStops != null && <div className="form-row">
      <div className="form-group col-md-10 offset-md-2">
        <Checkbox
          value={orderStop.AdjustSubsequentStops}
          onChange={(e) => updateSubsequentStops(e.value)}
          label={`Adjust all subsequent stops by ${fetchAdjustedScheduledDateTimeDiff()}`}
        />
      </div>
    </div>}

    {reasonsLate && reasonsLate.length > 0 && <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">Reason</label>
      </div>
      <div className="col-md-10">
        <DropDownList
          required={requireReasonLate}
          valid={!requireReasonLate || orderStop.ReasonLate > 0}
          value={reasonsLate.find(x => x.ID === orderStop.ReasonLate)}
          data={reasonsLate}
          textField="Name"
          dataItemKey="ID"
          style={{ width: '100%' }}
          onChange={(e) => setOrderStop({
            ...orderStop,
            ReasonLate: e.target.value.ID
          })}
        />
      </div>
    </div>}
    {orderStop.StopVerb === StopVerb.WINDOW && <>
      <div className="form-row">
        <div className="form-group col-md-2 text-right">
          <label className="col-form-label">Earliest</label>
        </div>
        <div className="form-group col-md-5">
          <SimpleDatePicker
            value={earliestDateTime?.toDate()}
            onChange={updateEarliestDate}
            inputProps={{
              valid: dateTime.isSameOrAfter(earliestDateTime, 'day'),
              prefix: () => <InputPrefix>
                <Button disabled>EST</Button>
              </InputPrefix>
            }}
          />
        </div>
        <div className="form-group col-md-5">
          <SimpleTimePicker
            value={earliestDateTime?.toDate()}
            onChange={updateEarliestTime}
            inputProps={{ valid: dateTime.isSameOrAfter(earliestDateTime, 'minute') }}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group col-md-2 text-right">
          <label className="col-form-label">Latest</label>
        </div>
        <div className="form-group col-md-5">
          <SimpleDatePicker
            value={latestDateTime?.toDate()}
            onChange={updateLatestDate}
            inputProps={{
              valid: dateTime.isSameOrBefore(latestDateTime, 'day'),
              prefix: () => <InputPrefix>
                <Button disabled>EST</Button>
              </InputPrefix>
            }}
          />
        </div>
        <div className="form-group col-md-5">
          <SimpleTimePicker
            value={latestDateTime?.toDate()}
            onChange={updateLatestTime}
            inputProps={{ valid: dateTime.isSameOrBefore(latestDateTime, 'minute') }}
          />
        </div>
      </div>
    </>}
    <div className="form-row">
      <div className="form-group col-md-2 text-right">
        <label className="col-form-label">Contact</label>
      </div>
      <div className="form-group col-md-10">
        <div className="input-group">
          <CustomerContactInput
            CustomerID={orderStop.CustomerID}
            CustomerContactID={orderStop.CustomerContactID}
            CustomerContacts={orderStop.CustomerContacts}
            onChange={(e) => {
              if (e.CustomerID) {
                setOrderStop({
                  ...orderStop,
                  CustomerID: e.CustomerID,
                  CustomerNumber: e.CustomerNumber,
                  CustomerContactID: e.CustomerContactID,
                  CustomerContacts: []
                });
                populateCustomerContacts(e.CustomerID);
              } else {
                setOrderStop({
                  ...orderStop,
                  CustomerContactID: e.CustomerContactID
                });
              }
            }}
          />
        </div>
      </div>
    </div>
  </>
}

export default StopEditUI;
